import type { components } from '@joyful/openapi/types.gen'
import type { EnumValueMeta } from '@joyful/utils'
import { createEnumSchema, parseTemporal } from '@joyful/utils'
import { Temporal } from '@js-temporal/polyfill'

export type OutboundPacket = components['schemas']['OutboundPacketRead']
export type OutboundPacketStatus = components['schemas']['OutboundPacketStatus']
export type OutboundPacketType = components['schemas']['OutboundPacketType']
export type OutboundPacketSubmissionMethod =
  components['schemas']['OutboundPacketSubmissionMethod']
export type OutboundPacketDeliveryStatus = 'DISPATCHING' | 'SENT' | 'FAILED'

export interface OutboundPacketDestinationData {
  appeal_type_display_name?: string
  attention?: string
  attention_to?: string
  city?: string
  denial_reason?: string
  destinationFaxNumber?: string
  destination_fax_number?: string
  destination_name?: string
  faxNumber?: string
  fax_number?: string
  line1?: string
  line2?: string
  name?: string
  notes?: string
  payer_name?: string
  portal_name?: string
  portal_notes?: string
  portal_url?: string
  postal_code?: string
  recipient_address_lines?: Array<string>
  recipient_name?: string
  reference?: string
  return_address_id?: string
  sender_address_lines?: Array<string>
  sender_name?: string
  state?: string
  url?: string
}

export const OutboundPacketUploadFileAccept = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/tiff': ['.tif', '.tiff'],
  'image/webp': ['.webp'],
}

export interface OutboundPacketDeliveryDetails {
  channel: 'mail' | 'fax' | 'portal'
  failureReason: string | null
  status: OutboundPacketDeliveryStatus
}

export const OutboundPacketTypeSchema = createEnumSchema<OutboundPacketType>([
  { title: 'Appeal packet', value: 'APPEAL_PACKET' },
  { title: 'Paper claim', value: 'PAPER_CLAIM' },
  { title: 'Medical records', value: 'MEDICAL_RECORDS' },
])

export const OutboundPacketStatusSchema =
  createEnumSchema<OutboundPacketStatus>([
    { title: 'Draft', value: 'DRAFT' },
    { title: 'Needs Review', value: 'NEEDS_REVIEW' },
    { title: 'Approved', value: 'APPROVED' },
    { title: 'Submitted', value: 'FINALIZED' },
    { title: 'Discarded', value: 'DISCARDED' },
  ])

export const OutboundPacketSubmissionMethodSchema =
  createEnumSchema<OutboundPacketSubmissionMethod>([
    { title: 'Mail', value: 'MAIL' },
    { title: 'Fax', value: 'FAX' },
    { title: 'Portal upload', value: 'PORTAL_UPLOAD' },
  ])

export type AppealPortalProvider =
  | 'availity'
  | 'cigna'
  | 'other'
  | 'united_healthcare'

interface AppealPortalProviderMetadata extends EnumValueMeta {
  aliases: ReadonlyArray<string>
  notes: string
  url: string
}

export const AppealPortalProviderSchema = createEnumSchema<
  AppealPortalProvider,
  AppealPortalProviderMetadata
>([
  {
    aliases: ['Availity Essentials', 'essentials.availity.com'],
    notes: 'Availity appeal tool',
    title: 'Availity',
    url: 'https://essentials.availity.com',
    value: 'availity',
  },
  {
    aliases: ['UHC', 'UnitedHealthcare', 'United Health Care'],
    notes: 'UnitedHealthcare provider portal',
    title: 'United Healthcare',
    url: 'https://secure.uhcprovider.com',
    value: 'united_healthcare',
  },
  {
    aliases: ['Cigna for HCP', 'CignaforHCP'],
    notes: 'Cigna for HCP portal',
    title: 'Cigna',
    url: 'https://cignaforhcp.cigna.com',
    value: 'cigna',
  },
  {
    aliases: [],
    notes: '',
    title: 'Other',
    url: '',
    value: 'other',
  },
])

export function resolveAppealPortalProviderValue(portalName: string) {
  const normalizedPortalName = portalName.trim().toLowerCase()
  if (!normalizedPortalName) {
    return undefined
  }

  const matchingProvider = AppealPortalProviderSchema.definitions.find(
    (definition) => {
      if (definition.value === 'other') {
        return false
      }

      const candidateNames = [definition.title, ...definition.aliases]
      return candidateNames.some((candidateName) => {
        return candidateName.toLowerCase() === normalizedPortalName
      })
    },
  )
  if (matchingProvider) {
    return matchingProvider.value
  }

  return 'other'
}

export function isOutboundPacketActiveDraft(packet: {
  status: OutboundPacket['status']
}) {
  return (
    packet.status === 'DRAFT' ||
    packet.status === 'NEEDS_REVIEW' ||
    packet.status === 'APPROVED'
  )
}

export function canRetryFailedSend(packet: OutboundPacket) {
  return (
    // oxlint-disable-next-line joyful/prefer-boolean-check
    packet.status === 'FINALIZED' && packet.latest_delivery?.can_retry === true
  )
}

export function getActiveDraftPacketOfType(
  packets: ReadonlyArray<OutboundPacket>,
  packetType: OutboundPacketType,
): OutboundPacket | null {
  const activeDraftPacket = packets.find((packet) => {
    return (
      isOutboundPacketActiveDraft(packet) && packet.packet_type === packetType
    )
  })
  return activeDraftPacket ?? null
}

export type OutboundPacketStartResolution =
  | { action: 'open'; packet: OutboundPacket }
  | { action: 'create' }

export function resolveOutboundPacketStart({
  packetType,
  packets,
}: {
  packetType: OutboundPacketType
  packets: ReadonlyArray<OutboundPacket>
}): OutboundPacketStartResolution {
  const activeDraftPacket = getActiveDraftPacketOfType(packets, packetType)
  if (activeDraftPacket !== null) {
    return {
      action: 'open',
      packet: activeDraftPacket,
    }
  }

  return {
    action: 'create',
  }
}

export function findAppealPacketSubmittedWithinMinutes(
  packets: ReadonlyArray<OutboundPacket>,
  minutes: number,
): string | null {
  const recentSubmittedAt = packets
    .filter((packet) => {
      return isSubmittedAppealPacket(packet)
    })
    .map((packet) => {
      return readOutboundPacketSubmissionAttemptAt(packet)
    })
    .find((submittedAt) => {
      return (
        submittedAt !== null &&
        isTimestampWithinPastMinutes(submittedAt, minutes)
      )
    })
  return recentSubmittedAt ?? null
}

export function requiresReview(
  packet: Pick<OutboundPacket, 'packet_type' | 'review_status'>,
) {
  const packetTypeRequiresReview =
    packet.packet_type === 'APPEAL_PACKET' ||
    packet.packet_type === 'MEDICAL_RECORDS'
  if (packetTypeRequiresReview) {
    return true
  }

  return packet.review_status !== 'NOT_REQUIRED'
}

export function readOutboundPacketDeliveryDetails(
  packet: Pick<OutboundPacket, 'latest_delivery'>,
): OutboundPacketDeliveryDetails | null {
  const latestDelivery = packet.latest_delivery
  if (!latestDelivery) {
    return null
  }

  const normalizedStatus = normalizeDeliveryStatus(latestDelivery.status)
  if (normalizedStatus === null) {
    return null
  }

  return {
    channel: latestDelivery.channel,
    failureReason: latestDelivery.failure_reason?.trim() ?? null,
    status: normalizedStatus,
  }
}

export function readOutboundPacketDestinationName(
  packet: Pick<OutboundPacket, 'destination_data'>,
) {
  const destinationData = packet.destination_data
  if (!destinationData || typeof destinationData !== 'object') {
    return null
  }

  const payerName = destinationData.payer_name
  if (typeof payerName === 'string' && payerName.trim().length > 0) {
    return payerName.trim()
  }

  const destinationName = destinationData.destination_name
  if (
    typeof destinationName === 'string' &&
    destinationName.trim().length > 0
  ) {
    return destinationName.trim()
  }

  const name = destinationData.name
  if (typeof name === 'string' && name.trim().length > 0) {
    return name.trim()
  }

  const portalName = destinationData.portal_name
  if (typeof portalName === 'string' && portalName.trim().length > 0) {
    return portalName.trim()
  }

  return null
}

export type OutboundPacketHistoryStatusTone =
  | 'error'
  | 'neutral'
  | 'success'
  | 'warning'

interface OutboundPacketDeliveryPresentationMeta extends EnumValueMeta {
  timestampFields: ReadonlyArray<'failed_at' | 'sent_at' | 'submitted_at'>
  tone: Exclude<OutboundPacketHistoryStatusTone, 'neutral'>
}

export const OutboundPacketDeliveryPresentationSchema = createEnumSchema<
  OutboundPacketDeliveryStatus | 'NONE',
  OutboundPacketDeliveryPresentationMeta
>([
  {
    timestampFields: ['submitted_at'],
    title: 'Sending',
    tone: 'warning',
    value: 'DISPATCHING',
  },
  {
    timestampFields: ['failed_at', 'submitted_at'],
    title: 'Failed to deliver',
    tone: 'error',
    value: 'FAILED',
  },
  {
    timestampFields: ['submitted_at', 'sent_at'],
    title: 'Submitted',
    tone: 'success',
    value: 'NONE',
  },
  {
    timestampFields: ['sent_at', 'submitted_at'],
    title: 'Sent',
    tone: 'success',
    value: 'SENT',
  },
])

export interface OutboundPacketHistoryStatus {
  label: string
  showBadge: boolean
  timestamp: string | null
  tone: OutboundPacketHistoryStatusTone
}

export function readOutboundPacketHistoryStatus(
  packet: Pick<OutboundPacket, 'latest_delivery' | 'status'>,
): OutboundPacketHistoryStatus {
  if (packet.status !== 'FINALIZED') {
    return {
      label: OutboundPacketStatusSchema.meta(packet.status).title,
      showBadge: false,
      timestamp: null,
      tone: 'neutral',
    }
  }

  return readFinalizedOutboundPacketHistoryStatus(packet)
}

function readFinalizedOutboundPacketHistoryStatus(
  packet: Pick<OutboundPacket, 'latest_delivery'>,
): OutboundPacketHistoryStatus {
  const deliveryStatus =
    readOutboundPacketDeliveryDetails(packet)?.status ?? 'NONE'
  const presentation =
    OutboundPacketDeliveryPresentationSchema.meta(deliveryStatus)

  return {
    label: presentation.title,
    showBadge: true,
    timestamp: readFinalizedOutboundPacketDeliveryTimestamp({
      deliveryStatus,
      latestDelivery: packet.latest_delivery,
    }),
    tone: presentation.tone,
  }
}

type OutboundPacketLatestDelivery = OutboundPacket['latest_delivery']

function readFinalizedOutboundPacketDeliveryTimestamp({
  deliveryStatus,
  latestDelivery,
}: {
  deliveryStatus: OutboundPacketDeliveryStatus | 'NONE'
  latestDelivery: OutboundPacketLatestDelivery
}) {
  if (!latestDelivery) {
    return null
  }

  const timestampFields =
    OutboundPacketDeliveryPresentationSchema.meta(
      deliveryStatus,
    ).timestampFields
  const timestampField = timestampFields.find((field) => {
    return latestDelivery[field]
  })

  return timestampField ? latestDelivery[timestampField] : null
}

export function readOutboundPacketSentAt(
  packet: Pick<
    OutboundPacket,
    | 'created_at'
    | 'latest_delivery'
    | 'modified_at'
    | 'status'
    | 'submission_method'
  >,
) {
  const latestDelivery = packet.latest_delivery
  if (latestDelivery?.sent_at) {
    return latestDelivery.sent_at
  }

  if (
    packet.status === 'FINALIZED' &&
    packet.submission_method === 'PORTAL_UPLOAD'
  ) {
    return packet.modified_at ?? packet.created_at ?? null
  }

  return null
}

function readOutboundPacketSubmissionAttemptAt(
  packet: Pick<
    OutboundPacket,
    | 'created_at'
    | 'latest_delivery'
    | 'modified_at'
    | 'status'
    | 'submission_method'
  >,
) {
  const latestDelivery = packet.latest_delivery
  if (latestDelivery?.sent_at) {
    return latestDelivery.sent_at
  }

  if (latestDelivery?.submitted_at) {
    return latestDelivery.submitted_at
  }

  if (
    packet.status === 'FINALIZED' &&
    packet.submission_method === 'PORTAL_UPLOAD'
  ) {
    return packet.modified_at ?? packet.created_at ?? null
  }

  return null
}

function isSubmittedAppealPacket(packet: OutboundPacket) {
  return (
    packet.packet_type === 'APPEAL_PACKET' &&
    packet.status === 'FINALIZED' &&
    readOutboundPacketDeliveryDetails(packet)?.status !== 'FAILED'
  )
}

function isTimestampWithinPastMinutes(timestamp: string, minutes: number) {
  const parsedTimestamp = parseTemporal(timestamp)
  if (!(parsedTimestamp instanceof Temporal.PlainDateTime)) {
    return false
  }

  const elapsedMinutes = parsedTimestamp
    .until(Temporal.Now.plainDateTimeISO())
    .total('minutes')
  return elapsedMinutes >= 0 && elapsedMinutes <= minutes
}

function normalizeDeliveryStatus(
  status: string,
): OutboundPacketDeliveryStatus | null {
  const uppercaseStatus = status.toUpperCase()
  if (uppercaseStatus === 'SENT') {
    return 'SENT'
  }

  if (uppercaseStatus === 'FAILED') {
    return 'FAILED'
  }

  if (
    uppercaseStatus === 'DISPATCHING' ||
    uppercaseStatus === 'PENDING' ||
    uppercaseStatus === 'SUBMITTED'
  ) {
    return 'DISPATCHING'
  }

  return null
}
